/**
 * Public API of the impersonation engine. Import from `@/shared/impersonation`
 * rather than reaching into individual files.
 *
 * ── Module map (where to look when debugging) ─────────────────────────────────
 *   types.ts        — the shapes: ImpersonationKind / ImpersonationTarget / State.
 *   registry.ts     — the ONE place per-role behavior lives (entryRoute / routePrefix
 *                     / headers / theme).
 *   permissions.ts  — the declarative policy (who may impersonate what) + helpers.
 *   core.ts         — the pure CORE (no React): persistence, state transitions
 *                     (push/pop/refine), the derivation (deriveImpersonationView =
 *                     "current identity", top-of-stack), and the network identity
 *                     (getImpersonationHeaders / withImpersonationKey = whole chain).
 *   ImpersonationProvider.tsx — the React shell: holds state, runs effects + the
 *                     nav-exit guard, and does the side-effects (routing / theme /
 *                     query cancellation). It calls core.ts for all logic.
 *   useImpersonation.ts — the accessor hook.
 *
 * Why core is separate from the provider: client.ts (GraphQL) and customerio.ts
 * import header/snapshot logic and must NOT pull in the "use client" provider, so
 * the pure logic can't live in the provider file.
 *
 * Two notions of "who am I impersonating": the hook/snapshot expose the CURRENT
 * identity (top of stack); the FULL CHAIN is exclusively getImpersonationHeaders /
 * withImpersonationKey (request headers + cache keys).
 */

// Provider + hook (the runtime entry points).
export { ImpersonationProvider } from "./ImpersonationProvider";
export { useImpersonation } from "./useImpersonation";

// Core types.
export type {
  ImpersonationKind,
  ImpersonationTarget,
  ImpersonationLayer,
  ImpersonationState,
  ImpersonationContextValue,
} from "./types";

// Role registry (the one place role-specific behavior lives).
export { ROLE_REGISTRY, type RoleSpec } from "./registry";

// Permission model.
export {
  DEFAULT_POLICY,
  isImpersonationAllowed,
  resolveActor,
  mapUserRoleToActorRole,
  type ActorRole,
  type ImpersonationPolicy,
  type PermissionDeniedPayload,
} from "./permissions";

// Core (pure, no React). React reads derived values off `useImpersonation()`;
// non-React callers use `getImpersonationSnapshot()` (point-in-time, same shape).
// `getImpersonationHeaders` (request headers) + `withImpersonationKey` (cache keys)
// carry the whole-chain identity. (The `deriveImpersonationView` / push/pop/refine
// internals are consumed by the provider directly, not re-exported.)
export {
  readState,
  writeState,
  clearState,
  getImpersonationHeaders,
  getImpersonationSnapshot,
  withImpersonationKey,
} from "./core";
